<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';

import { useBeneficiariesGroupStore } from '@peanuds/shared-state';
import QuickTransferSkeleton from './QuickTransferSkeleton.vue';
import {
  EXISTING_CONTACT_DETAILS_STEP,
  FLOW_KEYS,
  SEND_MONEY_FLOW_START_STEP,
} from '../send-money/internal/send-money.constants';

import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import InputText from 'primevue/inputtext';
import { AddPlusCircleBlackIcon as AddPlusCircleIcon, UsersGroupIcon } from '@peanuds/ui';

import type { BeneficiaryListItem } from '@peanuds/shared-state';
import { useAccessControlStore } from '@peanuds/app-shell';

const router = useRouter();
const beneficiariesGroupStore = useBeneficiariesGroupStore();
const { groupedBeneficiaries, selectedBeneficiary, isLoading, isSuccess } =
  storeToRefs(beneficiariesGroupStore);

const accessControlStore = useAccessControlStore();
const { isOnboarded } = storeToRefs(accessControlStore);

const searchQuery = ref('');

const searchLower = computed(() => searchQuery.value.trim().toLowerCase());

const filteredGroupedBeneficiaries = computed(() => {
  if (!searchLower.value) {
    return groupedBeneficiaries.value;
  }

  return groupedBeneficiaries.value.map((group) => {
    const filteredItems = group.items
      .filter((item) => item.label.toLowerCase().includes(searchLower.value))
      .sort((a, b) => a.label.localeCompare(b.label));

    return {
      ...group,
      items: filteredItems,
    };
  });
});

const hasFilteredBeneficiaries = computed(() => {
  return filteredGroupedBeneficiaries.value.some((group) => group.items.length > 0);
});

const getInitial = (name: string): string => {
  return name.charAt(0).toUpperCase();
};

const handleBeneficiaryClick = (beneficiary: BeneficiaryListItem) => {
  selectedBeneficiary.value = { key: beneficiary.key };
  router.push({
    name: FLOW_KEYS['existing-contact'],
    params: { step: EXISTING_CONTACT_DETAILS_STEP },
  });
};

const handleAddClick = () => {
  router.push({
    name: FLOW_KEYS['new-recipient'],
    params: { step: SEND_MONEY_FLOW_START_STEP },
  });
};
</script>

<template>
  <div class="quick-transfer" data-testid="dashboard-quick-transfer">
    <div class="quick-transfer__container">
      <div class="quick-transfer__header">
        <h2 class="quick-transfer__title">{{ $t('money.quick_transfer.title') }}</h2>
        <Button
          rounded
          text
          severity="secondary"
          class="quick-transfer__add-button"
          :aria-label="$t('money.quick_transfer.aria_open_account')"
          :disabled="!isOnboarded"
          iconOnly
          @click="handleAddClick"
        >
          <template #icon>
            <AddPlusCircleIcon />
          </template>
        </Button>
      </div>

      <div class="quick-transfer__search">
        <IconField>
          <InputIcon class="pi pi-search" />
          <InputText
            fluid
            v-model="searchQuery"
            :placeholder="$t('money.quick_transfer.search_placeholder')"
            :pt="{
              root: { style: { borderRadius: 'var(--p-border-radius-absolut)' } },
            }"
          />
        </IconField>
      </div>

      <div class="quick-transfer__content">
        <div v-if="isLoading" class="quick-transfer__loading"><QuickTransferSkeleton /></div>

        <div v-else-if="isSuccess && !hasFilteredBeneficiaries" class="quick-transfer__empty">
          <div class="quick-transfer__empty-icon">
            <UsersGroupIcon />
          </div>

          <p class="quick-transfer__empty-text">
            {{ $t('money.quick_transfer.no_contact_found') }}
          </p>
        </div>

        <template v-for="group in filteredGroupedBeneficiaries" :key="group.code">
          <div v-if="group.items.length > 0" class="quick-transfer__section">
            <div class="quick-transfer__section-header">
              <p class="quick-transfer__section-title">{{ group.label }}</p>
            </div>

            <div class="quick-transfer__contacts">
              <button
                v-for="contact in group.items"
                :key="contact.key"
                class="quick-transfer__contact"
                data-testid="dashboard-quick-transfer-contact"
                type="button"
                @click="handleBeneficiaryClick(contact)"
              >
                <Avatar
                  class="quick-transfer__avatar"
                  :label="getInitial(contact.label)"
                  shape="circle"
                />
                <div class="quick-transfer__contact-info">
                  <p class="quick-transfer__contact-name">{{ contact.label }}</p>
                </div>
              </button>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.quick-transfer {
  height: 100%;
  display: flex;
  flex-direction: column;

  &__container {
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: $space-sm;
    padding: $static-space-4;
    border-radius: var(--p-border-radius-xl);
    border: 1px solid var(--p-form-field-border-color, #cbd5e1);
    background-color: var(--p-primary-contrast-color, #ffffff);
  }

  &__header {
    display: flex;
    align-items: center;
    gap: $static-space-1;
  }

  &__title {
    color: var(--p-text-color, #334155);
    font-size: $static-text-md;
    font-weight: $font-regular;
    line-height: 1;
    letter-spacing: -0.064px;
    margin: 0;
    flex: 1;
  }

  &__add-button {
    width: 38px; // Fixed size from Figma spec
    height: 38px;
  }

  &__search {
    :deep(.p-inputtext) {
      padding-inline-start: f(36, 42);
    }

    :deep(.p-input-icon) {
      left: $space-sm;
      color: var(--p-inputtext-placeholder-color, #64748b);
    }

    margin-bottom: $space-md;

    :deep(.p-iconfield .p-inputtext:not(:first-child)) {
      padding-inline-start: f(32, 36);
    }

    :deep(.p-togglebutton-label) {
      font-size: $text-sm;
    }

    :deep(.p-iconfield .p-inputicon) {
      font-size: $text-sm;
    }

    :deep(.p-iconfield .p-inputtext) {
      font-size: $text-sm;
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: $space-lg;
    overflow-y: auto;
    flex: 1;
    min-height: 0;
  }

  &__section {
    display: flex;
    flex-direction: column;
    gap: $space-xs;
  }

  &__section-header {
    padding: 0 $space-xs;
  }

  &__section-title {
    color: var(--p-text-color, #334155);
    font-size: $text-sm;
    font-weight: $font-regular;
    line-height: 1;
    letter-spacing: -0.056px;
    margin: 0;
  }

  &__contacts {
    display: flex;
    flex-direction: column;
    gap: 6px; // Fixed 6px gap from Figma spec - no matching token
  }

  &__contact {
    display: flex;
    align-items: center;
    gap: $space-xs;
    padding: $space-xs;
    border: none;
    background-color: var(--p-content-background, #ffffff);
    border-radius: var(--p-border-radius-xxxl, 24px);
    cursor: pointer;
    text-align: left;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: var(--p-content-hover-background, #f1f5f9);
    }

    &:focus-visible {
      outline: 2px solid var(--p-primary-500);
      outline-offset: 2px;
    }
  }

  &__avatar {
    width: 28px; // Fixed size from Figma spec - no matching token
    height: 28px;
    flex-shrink: 0;
    background-color: var(--p-avatar-background, #e2e8f0);

    :deep(.p-avatar-label) {
      color: var(--p-avatar-color, #334155);
      font-size: $text-sm;
      font-weight: $font-regular;
    }
  }

  &__contact-info {
    display: flex;
    flex-direction: column;
    gap: $static-space-1;
    flex: 1;
    min-width: 0;
  }

  &__contact-name {
    color: var(--p-text-color, #334155);
    font-size: $text-sm;
    font-weight: $font-regular;
    line-height: 1;
    letter-spacing: -0.056px;
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__empty {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: $space-sm;
    flex: 1;
    padding: $space-xs;
    border-radius: $radius-xl;
    background-color: var(--p-form-field-filled-hover-background, #f8fafc);
    max-height: fit-content;
  }

  &__empty-icon {
    width: 34px;
    height: 34px;
    border-radius: $radius-full;
    background-color: var(--p-surface-300, #cbd5e1);
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      transform: scale(1.125);

      :deep(path) {
        stroke: var(--p-surface-700, #334155);
      }
    }
  }

  &__empty-text {
    font-size: $static-text-md;
    font-weight: $font-medium;
    line-height: 1;
    letter-spacing: -0.064px;
    color: var(--p-text-color, #334155);
    margin: 0;
  }
}
</style>
